import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  AdminEditDrawer,
  AdminEmptyState,
  AdminField,
  AdminPageHeader,
  AdminPagination,
  paginateItems,
  previewText,
  useAdminQueryParam,
} from "@/pages/admin/AdminUx";

function krw(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return "-";
  return `${v.toLocaleString("ko-KR")}원`;
}

const EDITABLE = [
  "name",
  "name_en",
  "sales_description",
  "description",
  "unit",
  "pricing_type",
  "recommended_price_min",
  "recommended_price_max",
  "default_quantity",
  "cost_type",
  "billing_type",
  "payer",
  "cost_price",
  "internal_min_price",
  "internal_note",
  "duration_min_weeks",
  "duration_max_weeks",
];

export default function SalesPricesTab() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState(null);
  const [category, setCategory] = useAdminQueryParam("category", "all");

  const load = async () => {
    try {
      const r = await api.get("/admin/sales-catalog");
      setItems(r.data.items || []);
      setCategories(r.data.categories || []);
    } catch {
      toast.error("단가 목록 로드 실패");
    }
  };
  useEffect(() => { load(); }, []);

  const catLabel = (id) => categories.find((c) => c.id === id)?.label || id || "-";

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (items || []).filter((it) => {
      if (category && category !== "all" && it.category_id !== category) return false;
      if (!needle) return true;
      const hay = [it.name, it.code, it.quote_role, it.billing_type, it.sales_description, it.description]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(needle);
    });
  }, [items, category, q]);

  const paged = paginateItems(filtered, page, 20);
  useEffect(() => { setPage(1); }, [category, q]);
  useEffect(() => { if (page !== paged.page) setPage(paged.page); }, [page, paged.page]);

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const payload = {};
      EDITABLE.forEach((k) => {
        if (editing[k] !== undefined) payload[k] = editing[k];
      });
      ["recommended_price_min", "recommended_price_max", "default_quantity"].forEach((k) => {
        payload[k] = Number(payload[k]) || 0;
      });
      ["cost_price", "internal_min_price", "duration_min_weeks", "duration_max_weeks"].forEach((k) => {
        if (payload[k] === "" || payload[k] == null) payload[k] = null;
        else payload[k] = Number(payload[k]);
      });
      const r = await api.patch(`/admin/sales-catalog/${editing.id}`, payload);
      toast.success("저장됨");
      setItems((prev) => prev.map((it) => (it.id === editing.id ? { ...it, ...r.data } : it)));
      setEditing(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "저장 실패");
    }
    setSaving(false);
  };

  const setEdit = (key, val) => setEditing((prev) => ({ ...prev, [key]: val }));

  return (
    <div data-testid="sales-prices">
      <AdminPageHeader
        title="영업 단가 관리"
        description="영업 견적 계산에 사용되는 서비스와 권장 단가를 관리합니다. 기존 견적 스냅샷은 바뀌지 않습니다."
      />

      <div className="admin-chip-row" data-testid="sales-price-categories">
        <button
          type="button"
          className={`admin-chip${category === "all" || !category ? " is-on" : ""}`}
          onClick={() => setCategory("all")}
        >
          전체
        </button>
        {categories.map((c) => (
          <button
            key={c.id}
            type="button"
            className={`admin-chip${category === c.id ? " is-on" : ""}`}
            onClick={() => setCategory(c.id)}
          >
            {c.label}
          </button>
        ))}
      </div>

      <div className="admin-toolbar">
        <input
          className="admin-toolbar__search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="서비스명 / 코드 / 역할 검색"
          data-testid="sales-price-search"
        />
        <span className="admin-count">총 {items.length}개 · 현재 필터 {filtered.length}개</span>
      </div>

      {paged.items.length === 0 ? (
        <AdminEmptyState message="표시할 단가 항목이 없습니다." />
      ) : (
        <div className="admin-row-list">
          {paged.items.map((it) => (
            <div key={it.id} className="admin-row" data-testid={`sales-price-row-${it.code || it.id}`}>
              <div className="admin-row__main">
                <div className="admin-row__title">{it.name}</div>
                <div className="admin-row__meta">
                  {it.code} · {catLabel(it.category_id)} · {it.quote_role || "-"} · {krw(it.recommended_price_min)}
                  {Number(it.recommended_price_max) !== Number(it.recommended_price_min) ? ` ~ ${krw(it.recommended_price_max)}` : ""}
                  {" · "}{it.billing_type || "-"} · {it.active === false ? "비활성" : "활성"}
                </div>
                {it.sales_description ? <div className="admin-row__preview">{previewText(it.sales_description, 90)}</div> : null}
              </div>
              <div className="admin-row__actions">
                <button type="button" className="admin-row__btn" onClick={() => setEditing({ ...it })}>
                  수정
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <AdminPagination
        page={paged.page}
        pageSize={20}
        total={filtered.length}
        onPageChange={setPage}
      />

      <AdminEditDrawer
        open={!!editing}
        title="서비스 수정"
        onClose={() => setEditing(null)}
        testId="sales-price-drawer"
        footer={(
          <>
            <button type="button" className="admin-row__btn" onClick={() => setEditing(null)}>취소</button>
            <button type="button" disabled={saving} onClick={save} className="bg-white text-black rounded-lg px-4 py-2 text-sm" data-testid="sales-price-save">
              {saving ? "저장 중…" : "저장"}
            </button>
          </>
        )}
      >
        {editing ? (
          <>
            <AdminField label="서비스명">
              <input value={editing.name || ""} onChange={(e) => setEdit("name", e.target.value)} />
            </AdminField>
            <AdminField label="코드">
              <input value={editing.code || ""} readOnly />
            </AdminField>
            <AdminField label="카테고리">
              <input value={catLabel(editing.category_id)} readOnly />
            </AdminField>
            <AdminField label="Role">
              <input value={editing.quote_role || ""} readOnly />
            </AdminField>
            <AdminField label="최소 가격">
              <input type="number" value={editing.recommended_price_min ?? 0} onChange={(e) => setEdit("recommended_price_min", e.target.value)} data-testid="sales-price-min" />
            </AdminField>
            <AdminField label="최대 가격">
              <input type="number" value={editing.recommended_price_max ?? 0} onChange={(e) => setEdit("recommended_price_max", e.target.value)} data-testid="sales-price-max" />
            </AdminField>
            <AdminField label="billing">
              <input value={editing.billing_type || ""} onChange={(e) => setEdit("billing_type", e.target.value)} />
            </AdminField>
            <AdminField label="cost type">
              <input value={editing.cost_type || ""} onChange={(e) => setEdit("cost_type", e.target.value)} />
            </AdminField>
            <AdminField label="payer">
              <input value={editing.payer || ""} onChange={(e) => setEdit("payer", e.target.value)} />
            </AdminField>
            <AdminField label="unit">
              <input value={editing.unit || ""} onChange={(e) => setEdit("unit", e.target.value)} />
            </AdminField>
            <AdminField label="원가 (선택)">
              <input type="number" value={editing.cost_price ?? ""} onChange={(e) => setEdit("cost_price", e.target.value)} />
            </AdminField>
            <AdminField label="내부 최소 (선택)">
              <input type="number" value={editing.internal_min_price ?? ""} onChange={(e) => setEdit("internal_min_price", e.target.value)} />
            </AdminField>
            <AdminField label="description">
              <textarea rows={3} value={editing.sales_description || editing.description || ""} onChange={(e) => setEdit("sales_description", e.target.value)} />
            </AdminField>
            <AdminField label="suggest">
              <input value={Array.isArray(editing.suggest) ? editing.suggest.join(", ") : (editing.suggest || "")} readOnly />
            </AdminField>
          </>
        ) : null}
      </AdminEditDrawer>
    </div>
  );
}
